<script setup lang="ts">
import { ExternalLink } from 'lucide-vue-next';
import { computed } from 'vue';

import MicrosoftCapabilitiesSection from '@/components/settingsMenu/sections/microsoft-capabilities-ui/MicrosoftCapabilitiesSection.vue';
import SharePointConnectionSection from '@/components/settingsMenu/sections/sharepoint-connection-ui/SharePointConnectionSection.vue';
import { useAuthStore } from '@/stores/auth';
import { useTenantStore } from '@/stores/tenant';

const MICROSOFT_DOC_URL =
    'https://docs.elqano.com/playground/sharepoint-connection';

const tenantStore = useTenantStore();
const authStore = useAuthStore();

/** Set only once credentials came back and Microsoft returned an account for them. Without an
 *  account there is no token, and the connectors below have no granted scopes to read. */
const isConfigured = computed(
    () =>
        authStore.isMicrosoftCredentialsLoaded &&
        Boolean(authStore.microsoftClientId)
);
</script>

<template>
    <section class="microsoft-connection rounded-xl border p-5 space-y-6">
        <h3 class="text-md font-semibold">Microsoft</h3>

        <div v-if="!tenantStore.tenantId" class="text-sm text-muted-foreground">
            Select a workspace first to configure Microsoft.
        </div>

        <template v-else>
            <div class="space-y-4 rounded-lg bg-muted/30 p-4">
                <div class="space-y-1">
                    <h4 class="text-sm font-semibold">
                        Microsoft App registration
                        <span class="font-normal text-muted-foreground">
                            — needs a Microsoft Entra organisation account
                        </span>
                    </h4>
                    <p class="text-sm text-muted-foreground">
                        Register an application in your Entra tenant and paste
                        its identifiers here.
                        <span class="font-medium"
                            >With its client secret, Elqano reads SharePoint and
                            Azure AD groups. The Microsoft 365 connectors below
                            are granted on the same application, but run with
                            each member's own account, not the secret.</span
                        >
                    </p>
                    <a
                        :href="MICROSOFT_DOC_URL"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center text-sm underline underline-offset-4"
                    >
                        How to create the application
                        <ExternalLink class="ml-1 h-3 w-3" />
                    </a>
                </div>

                <SharePointConnectionSection />
            </div>

            <!-- Shown but inert until Microsoft is configured. -->
            <div
                :class="{ 'opacity-50': !isConfigured }"
                :inert="!isConfigured"
            >
                <MicrosoftCapabilitiesSection />
            </div>
        </template>
    </section>
</template>
